import { Icon } from "@mdi/react";
import { memo, useEffect, useMemo, useState } from "react";

import { mdiFormatPaint } from "@mdi/js";
import { cookies } from "constants/settings.constants";
import Cookies from "js-cookie";
import styled from "styled-components";
import { getStoredCustomColors } from "utils/color/getStoredCustomColors";

import { useExclusiveColorPicker } from "hooks/useExclusiveColorPicker";

import {
  ShapefileStyleProp,
  normalizeFillPattern,
  normalizeLineStyle
} from "components/project/shapefiles/shapefiles.constants";

import BaseMenu from "./BaseMenu";
import ColorChooser from "./ColorChooser";
import "./ColorPicker.scss";
import ColorPickerWarningMessage from "./ColorPickerWarningMessage";

const default_color = "#025f9c";

const MIN_FILL_THICKNESS = 0.5;

const isWhite = (color?: string) => color?.toUpperCase() === "#FFFFFF";

interface ColorPickerValues {
  color: string;
  strokeColor: string;
  thickness: number;
  lineStyle?: string;
  fillPattern?: string;
  opacity: number;
}
const transparent_color_selected_bg = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'><path d='M0 99 L99 0 L100 1 L1 100' fill='rgb(214,40,11)' /></svg>")`;

const ColorOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  & > div:first-child {
    padding: 16px 15px 0 18px;
  }
  & > div:last-child {
    padding: 10px 15px 17px 18px;
  }

  .ColorPicker {
    width: 18px;
    height: 18px;
    top: 3px;
  }
  .checkbox-input {
    .checkbox {
      width: 18px;
      height: 18px;
      margin: 0;
      margin-right: 8px;
    }
  }
  .thickness-dropdown {
    height: 28px;
  }
  span {
    margin-left: 12px;
    margin-right: 20px;
    font-size: 1.4rem;
    font-weight: var(--fontWeightMedium);
  }
  .BaseDropdown {
    min-width: 68px;
  }
`;

const FlexRow = styled.div`
  display: inline-flex;
  align-items: center;
  cursor: default;
`;

function ColorPicker({
  values = {
    color: default_color,
    strokeColor: default_color,
    thickness: 2,
    lineStyle: "solid",
    opacity: 100
  } as ColorPickerValues,
  place = "right",
  align = "start",
  onChange = null,
  onChangeOpacity = null,
  onChangeThickness = null,
  onChangeLineStyle = null,
  onChangeFillPattern = null,
  isAtFolderLevel = false,
  isAtAttributeLevel = false,
  isAtShapefileLevel = false,
  isGeoTIFF = false,
  isReadOnly = false,
  hasOutline = true,
  includeOpacity = true,
  includeFillThickness = false,
  disableTransparent = false,
  pinnedColor = undefined
}) {
  const {
    closeOthers,
    registerCloseFunction,
    closeOtherChoosers,
    registerChooserCloseFunction
  } = useExclusiveColorPicker();
  const [isOpen, setOpen] = useState(false);

  const toggle = isAtAttributeLevel ? () => setOpen(!isOpen) : undefined;

  const [currentValues, setValues] = useState(values);
  const [customColors, setCustomColors] = useState([]);

  function update(prop, value) {
    const newValues = { ...currentValues };
    newValues[prop] = value;
    setValues(newValues);

    if (onChangeOpacity && prop === ShapefileStyleProp.Opacity) {
      onChangeOpacity(prop, value);
    } else if (onChangeThickness && prop === ShapefileStyleProp.Thickness) {
      onChangeThickness(prop, value);
    } else if (onChangeLineStyle && prop === ShapefileStyleProp.LineStyle) {
      onChangeLineStyle(prop, value);
    } else if (onChangeFillPattern && prop === ShapefileStyleProp.FillPattern) {
      onChangeFillPattern(prop, value);
    } else {
      onChange?.(prop, value);
    }
  }

  useEffect(() => {
    const storedCustomColors = Cookies.get(cookies.CUSTOM_COLORS);
    if (storedCustomColors && !customColors.length)
      setCustomColors(getStoredCustomColors(storedCustomColors));
  }, [customColors]);

  // TODO: Fix infinite loop caused by this useEffect when no values are passed into the ColorPicker
  useEffect(() => {
    setValues(values);
  }, [values]);

  const styleMultiObject = (isDisabled) => {
    if (isGeoTIFF && isDisabled) {
      return {
        backgroundColor: "#FFFFFF",
        flex: "100%",
        borderRadius: "2px",
        border: "3px solid #000000",
        boxShadow: "",
        backgroundImage: "",
        cursor: "not-allowed"
      };
    }

    const styles = {
      backgroundColor: values.color,
      flex: "100%",
      borderRadius: "2px",
      border: "",
      boxShadow: "",
      backgroundImage: "",
      cursor: isDisabled ? "not-allowed" : "pointer"
    };

    if (isWhite(values.strokeColor)) {
      styles.border = "1px solid #D9E1E2";
      styles.boxShadow = isDisabled ? "" : `inset 0 0 0 ${values.thickness}px #FFFFFF`;
    } else {
      styles.border = `3px solid ${values.strokeColor}`;
    }

    if (values.strokeColor === "transparent" && values.color === "transparent") {
      styles.backgroundImage = transparent_color_selected_bg;
      styles.border = "1px solid #D9E1E2";
    }
    return styles;
  };

  const styleObject = (prop, isDisabled) => {
    const styles = {
      flex: "100%",
      borderRadius: "2px",
      border: `${values.thickness}px solid ${
        prop === ShapefileStyleProp.StrokeColor ? values.strokeColor : "transparent"
      }`,
      backgroundColor: prop === ShapefileStyleProp.Color ? values.color : "white",
      boxShadow: "",
      backgroundImage: "",
      cursor: isDisabled ? "not-allowed" : "pointer"
    };

    if (prop === ShapefileStyleProp.Color && isWhite(values.color)) {
      styles.backgroundColor = "white";
      styles.border = "1px solid #D9E1E2";
    } else if (prop === ShapefileStyleProp.StrokeColor && isWhite(values.strokeColor)) {
      styles.backgroundColor = "#D9E1E2";
      styles.border = "1px solid #D9E1E2";
      styles.boxShadow = isDisabled ? "" : `inset 0 0 0 ${values.thickness}px #FFFFFF`;
    }

    if (values[prop] === "transparent") {
      styles.backgroundImage = transparent_color_selected_bg;
      styles.border = "1px solid #D9E1E2";
    }
    return styles;
  };

  const warningMessage = isAtFolderLevel
    ? "This will set the colour for all shapefiles and GeoTIFFs in the folder."
    : isAtShapefileLevel && !isGeoTIFF
    ? "This will set the colour for all shapefile details in the shapefile."
    : "";

  const isInteractionDisabled = isReadOnly || isGeoTIFF;

  const classNames = useMemo(() => {
    function variation() {
      return `${place}-${align}`;
    }
    return `ColorPicker ${isOpen ? "isOpen" : ""} ${variation()} ${
      isAtFolderLevel ? "atFolderLevel" : ""
    } ${!isInteractionDisabled ? "hasAnimations" : ""}`;
  }, [align, isInteractionDisabled, isOpen, place]);

  const trigger = (prop) => {
    return (
      <div
        tabIndex={isInteractionDisabled ? -1 : 0}
        role="button"
        aria-disabled={isInteractionDisabled}
        className={classNames}>
        {prop === null ? (
          isAtFolderLevel ? (
            <ProjectFolderActionButton data-testid="color-picker-button">
              <Icon path={mdiFormatPaint} size={"18px"} />
              Set Shapefile Colours
            </ProjectFolderActionButton>
          ) : (
            <div
              className="flex flex-centered"
              data-testid="color-picker-button"
              style={styleMultiObject(isInteractionDisabled)}></div>
          )
        ) : (
          <div
            className="flex flex-centered"
            data-testid={`select-${prop}-button`}
            style={styleObject(prop, isInteractionDisabled)}></div>
        )}
      </div>
    );
  };

  // `onColorPicked` runs only when a swatch is chosen (the custom-colour screen
  // keeps its own state and never reports through onChange), so it is safe to
  // dismiss the menu with it.
  const renderFillColorChooser = (onColorPicked?: () => void) => (
    <ColorChooserWrapper className="colorChooserWrapper">
      <ColorChooser
        selectedColor={values.color}
        onChange={(color) => {
          update(ShapefileStyleProp.Color, color);
          onColorPicked?.();
        }}
        customColors={customColors}
        setCustomColors={setCustomColors}
        includeOpacity={includeOpacity}
        selectedOpacity={values.opacity}
        onChangeOpacity={(opacity) => {
          update(ShapefileStyleProp.Opacity, opacity);
        }}
        includeThickness={includeFillThickness}
        minThickness={includeFillThickness ? MIN_FILL_THICKNESS : undefined}
        selectedThickness={values.thickness}
        onChangeThickness={(thickness) => {
          update(ShapefileStyleProp.Thickness, thickness);
        }}
        includeFillPattern={ShapefileStyleProp.FillPattern in values}
        selectedFillPattern={normalizeFillPattern(values.fillPattern)}
        onChangeFillPattern={(pattern) => {
          update(ShapefileStyleProp.FillPattern, pattern);
        }}
        disableTransparent={disableTransparent}
        pinnedColor={pinnedColor}
      />
    </ColorChooserWrapper>
  );

  const fillChooser = (
    <FlexRow>
      <BaseMenu
        place={place}
        align={align}
        trigger={trigger("color")}
        onOpen={() => closeOtherChoosers("color")}>
        {({ closeMenu }) => {
          registerChooserCloseFunction("color", closeMenu);
          return renderFillColorChooser();
        }}
      </BaseMenu>
      <span>Fill</span>
    </FlexRow>
  );

  const outlineChooser = hasOutline ? (
    <FlexRow>
      <BaseMenu
        place={place}
        align={align}
        trigger={trigger("strokeColor")}
        onOpen={() => closeOtherChoosers("strokeColor")}>
        {({ closeMenu }) => {
          registerChooserCloseFunction("strokeColor", closeMenu);
          return (
            <ColorChooserWrapper className="colorChooserWrapper">
              <ColorChooser
                selectedColor={values.strokeColor}
                onChange={(color) => {
                  update(ShapefileStyleProp.StrokeColor, color);
                }}
                customColors={customColors}
                setCustomColors={setCustomColors}
                includeThickness={true}
                selectedThickness={values.thickness}
                onChangeThickness={(thickness) => {
                  update(ShapefileStyleProp.Thickness, thickness);
                }}
                includeLineStyle={ShapefileStyleProp.LineStyle in values}
                selectedLineStyle={normalizeLineStyle(values.lineStyle)}
                onChangeLineStyle={(style) => {
                  update(ShapefileStyleProp.LineStyle, style);
                }}
                disableTransparent={disableTransparent}
              />
            </ColorChooserWrapper>
          );
        }}
      </BaseMenu>
      <span>Outline</span>
    </FlexRow>
  ) : null;

  const colorOptions = (
    <>
      {warningMessage && <ColorPickerWarningMessage message={warningMessage} />}
      <ColorOptionsContainer
        className="colorOptionsContainer"
        data-testid="color-options-panel">
        {fillChooser}
        {outlineChooser}
      </ColorOptionsContainer>
    </>
  );

  // Without an outline chooser the Fill/Outline step holds a single row, so it
  // costs the user a click to reach the only thing they can change. Skip it and
  // open the colour chooser straight from the swatch. Kept for the warning-message
  // levels (folder/shapefile), where that step also carries the warning.
  const isSingleChooser = !hasOutline && !warningMessage;

  // Closing on select is only right when picking a colour is the *whole*
  // interaction. An opacity slider or fill-pattern grid in the same chooser
  // would be torn away mid-use, so those keep the menu open.
  const closesOnSelect =
    isSingleChooser &&
    !includeOpacity &&
    !includeFillThickness &&
    !(ShapefileStyleProp.FillPattern in values);

  return !isAtAttributeLevel ? (
    <BaseMenu
      place={place}
      align={align}
      disabled={isGeoTIFF}
      hasBlockLabel={isAtShapefileLevel}
      onOpen={() => {
        closeOthers();
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      toggle={toggle}
      trigger={trigger(null)}>
      {({ closeMenu }) => {
        registerCloseFunction(closeMenu);
        return (
          !isReadOnly &&
          (isSingleChooser
            ? renderFillColorChooser(closesOnSelect ? closeMenu : undefined)
            : colorOptions)
        );
      }}
    </BaseMenu>
  ) : isSingleChooser ? (
    // The attribute-level popover is owned by its caller, which closes itself
    // from the same onChange, so nothing to dismiss here.
    renderFillColorChooser()
  ) : (
    colorOptions
  );
}

const ColorChooserWrapper = styled.div`
  padding: 10px 16px;
`;

const ProjectFolderActionButton = styled.div`
  height: 3.2rem;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-50);
  font-weight: 500;
  padding: 0 var(--space-3);
  cursor: pointer;
  &:hover {
    color: ${(props) => (props.danger ? "var(--color-danger)" : "var(--color-primary)")};
  }
`;

export default memo(ColorPicker);
